/* eslint-disable no-console */
import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { loggingScope } from './application';

/**
 * Unified logging system for use throughout the app.
 * NOTE: All dev logs will be console logged to avoid spamming sentry and subsequently slack with fake alerts
 * If you want to log to sentry anyway you must remove the dev="true" attribute from the "rapidstor-v2-container" in index.html
 * @param defconLevel
 * @param message
 * https://docs.sentry.io/platforms/javascript/guides/react/usage/set-level/
 */
export interface LogTags {
    name: string;
    value: string | number | boolean;
}

export const logEvent = (
    defconLevel: number,
    message: string,
    location?: Location,
    tags?: LogTags[],
) => {
    // Don't change this to using `options.dev` for now, as it breaks tests due to document being undefined
    // in the options file.
    const root = document.getElementById('rapidstor-v2-frontend');
    const dev = root?.getAttribute('dev');

    const scope = loggingScope();

    if (location) {
        scope.setTag('rapidstor_locationCode', location.locationCode);
        scope.setTag('rapidstor_corpCode', location.corpCode);
    }

    const context = message.match(/\[(.*?)\]/);

    if (context) {
        // eslint-disable-next-line prefer-destructuring
        scope.setTag('log_origination', context[0]);
    }

    let shouldAlert = false;

    if (tags) {
        tags.forEach((tag) => {
            if (tag.name === 'wizard_test' && tag.value === true) {
                shouldAlert = true;
            }
            scope.setTag(tag.name, tag.value);
        });
    }

    scope.setTag('page', window.location.hash);

    switch (defconLevel) {
        case 1:
            if (dev) {
                console.error(
                    `%c[DEV] ${message}`,
                    'background: #444; color: #fff; font-size: 16px; display:block; border-radius:8px; padding: 4px;',
                );
            } else {
                scope.captureMessage(message, 'fatal');
            }
            break;

        case 2:
            if (dev) {
                console.warn(
                    `%c[DEV] ${message}`,
                    'background: #444; color: #fff; font-size: 16px; display:block; border-radius:8px; padding: 4px;',
                );
            } else {
                scope.captureMessage(message, 'warning');
            }

            break;

        case 3:
            console.info(
                `%c[DEV] ${message}`,
                'background: #444; color: #fff; font-size: 16px; display:block; border-radius:8px; padding: 4px;',
            );
            if (shouldAlert) {
                scope.captureMessage(message, 'info');
            }
            break;
        default:
            break;
    }
};

class Logger {
    public static error(message: string, location?: Location, tags?: LogTags[]) {
        logEvent(1, message, location, tags);
    }

    public static warn(message: string, location?: Location, tags?: LogTags[]) {
        logEvent(2, message, location, tags);
    }

    public static info(message: string, location?: Location, tags?: LogTags[]) {
        logEvent(3, message, location, tags);
    }
}

export default Logger;
